import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../store/utility';
import type { ChannelPreference, TypePreference } from '../components/preferenceTypes';

interface UnreadNotification {
    id: number;
}

/** The whole preferences object, as `/preferences/` returns it from both verbs. */
export interface PreferencesPayload {
    id: number;
    subs_nodes: number[];
    disable_nodes: number[];
    disabled_countries: string[];
    disabled_tags: string[];
    channels: ChannelPreference[];
    types: TypePreference[];
}

interface PreferencesResponse {
    msg: string;
    data: PreferencesPayload;
}

/**
 * The role's preset, in the same shape the preferences themselves come in: `enabled` and
 * `min_severity` say what the user's role would get, not what this user has set.
 */
interface PreferenceDefaultsResponse {
    msg: string;
    data: {
        channels: ChannelPreference[];
        types: TypePreference[];
    };
}

/** A POST touches only the fields it names, so every one but the target user is optional. */
export interface PreferencesUpdate extends Partial<Omit<PreferencesPayload, 'id'>> {
    user_id: number;
}

export const notificationsAPI = createApi({
    reducerPath: 'notificationsAPI',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/notifications/' }),
    tagTypes: ['Notification', 'NotificationPreference'],
    endpoints: (builder) => ({
        getUnread: builder.query<UnreadNotification[], Record<string, unknown> | void>({
            query: (params) => ({
                url: 'unread/list/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map((notification) => ({ type: 'Notification' as const, id: notification.id })),
                          'Notification',
                      ]
                    : ['Notification'],
        }),

        getPreferences: builder.query<PreferencesResponse, { user_id: number }>({
            query: (params) => ({
                url: 'preferences/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
            providesTags: (result) =>
                result ? [{ type: 'NotificationPreference' as const, id: result.data.id }] : ['NotificationPreference'],
        }),
        // No tags: a role's preset does not change under a save.
        getPreferenceDefaults: builder.query<PreferenceDefaultsResponse, { user_id: number }>({
            query: (params) => ({
                url: 'preferences/defaults/',
                method: 'GET',
                withCredentials: true,
                params: params,
            }),
        }),
        // No `invalidatesTags`: the POST answers with the whole object, and
        // `useNotificationPreferences` writes the cache from that response.
        setPreferences: builder.mutation<PreferencesResponse, PreferencesUpdate>({
            query: (data) => ({
                url: 'preferences/',
                method: 'POST',
                withCredentials: true,
                data: data,
            }),
        }),
    }),
});

export const {
    endpoints,
    useGetUnreadQuery,
    useGetPreferencesQuery,
    useGetPreferenceDefaultsQuery,
    useSetPreferencesMutation,
} = notificationsAPI;

export const notificationAPIReducer = notificationsAPI.reducer;
export const notificationAPIReducerPath = notificationsAPI.reducerPath;
export const notificationAPIReducerMiddleware = notificationsAPI.middleware;
